import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { onboardErrorMessage } from "./onboardErrors";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useAuth } from "../../context/AuthContext";
import { CategoryCheckboxTree } from "../services/ui/CategoryCheckboxTree";
import { RawCategory } from "../services/utils/buildCategoryTree";
import { formatDate } from "../../lib/formatDate";
import { isValidRegistryCode, lookupErrorKey } from "./onboardingErrors";

const API_URL = process.env.REACT_APP_API_URL || "";

type ServiceCategory = RawCategory;

type LookupNew = {
  alreadyExists: false;
  registryCode: string;
  name: string;
  address: string | null;
  postalCode: string | null;
  email: string | null;
  phone: string | null;
  foundingDate: string | null;
  legalForm?: string | null;
  /** Whether the registry scrape filled the fields, or the session data is all there is. */
  registryAutofill?: "ok" | "not_found" | "unavailable";
  availableServices: ServiceCategory[];
};

type LookupExisting = { alreadyExists: true; providerId: string; name: string };

type LookupData = LookupNew | LookupExisting;

type Step = "loading" | "form" | "confirm" | "submitting" | "success" | "failed";

export function OnboardingProviderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { refreshAuth } = useAuth();

  const registryCode = searchParams.get("registryCode") || "";

  const [step, setStep] = useState<Step>("loading");
  const [data, setData] = useState<LookupData | null>(null);
  const [email, setEmail] = useState("");
  const [services, setServices] = useState<string[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!registryCode) {
      navigate("/select-role");
      return;
    }
    fetchLookup();
  }, [registryCode]);

  const fetchLookup = async () => {
    setStep("loading");
    setError("");

    // The registry search matches substrings, so a mistyped code must not even
    // be sent (BUG-17).
    if (!isValidRegistryCode(registryCode)) {
      setError(t(lookupErrorKey("invalid_registry_code")));
      setStep("failed");
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/api/service-providers/lookup/${registryCode}`, {
        withCredentials: true,
      });
      if (res.data.success) {
        setData(res.data.data);
        if (res.data.data.alreadyExists) setStep("confirm");
        else {
          setEmail(res.data.data.email || "");
          setStep("form");
        }
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        toast.error(t("onboarding.provider.errors.notInSession"));
        navigate("/select-role");
        return;
      }
      // Without a terminal state the page kept spinning behind an error nobody
      // could see — the banner is only rendered in the form/confirm steps.
      setError(t(lookupErrorKey(err.response?.data?.code)));
      setStep("failed");
    }
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canSubmit = emailValid && services.length > 0 && step === "form";

  const handleSubmit = async () => {
    if (!data || data.alreadyExists) return;
    setStep("submitting");
    setError("");
    try {
      const res = await axios.post(
        `${API_URL}/api/service-providers/onboard`,
        { registryCode: data.registryCode, email, services },
        { withCredentials: true }
      );
      if (res.data.success) {
        setStep("success");
        setTimeout(async () => {
          await refreshAuth();
          navigate("/dashboard", { replace: true });
        }, 1500);
      }
    } catch (err: any) {
      setError(onboardErrorMessage(err, t));
      setStep("form");
    }
  };

  const handleConfirmJoin = async () => {
    if (!data || !data.alreadyExists) return;
    setStep("submitting");
    setError("");
    try {
      const res = await axios.post(
        `${API_URL}/api/service-providers/onboard`,
        { registryCode },
        { withCredentials: true }
      );
      if (res.data.success) {
        setStep("success");
        setTimeout(async () => {
          await refreshAuth();
          navigate("/dashboard", { replace: true });
        }, 1500);
      }
    } catch (err: any) {
      setError(onboardErrorMessage(err, t));
      setStep("confirm");
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 bg-card px-4 sm:px-6 py-5 flex flex-col">
        <Link to="/" className="w-fit group flex items-center gap-2">
          <img src="/logo.png" alt="Unikort" className="h-9 w-auto" />
          <span className="text-xl font-semibold text-[#5756d6] dark:text-[#777aff]">Unikort</span>
        </Link>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[560px]">
            {step === "loading" && (
              <div className="flex flex-col items-center py-16 gap-4">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
                <p className="text-muted-foreground text-sm">{t("onboarding.loading")}</p>
              </div>
            )}

            {step === "failed" && (
              <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-xl border border-destructive/20 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {step === "form" && data && !data.alreadyExists && (
              <>
                <h1 className="text-2xl font-semibold mb-1">{t("onboarding.provider.title")}</h1>
                <p className="text-sm text-muted-foreground mb-6">{data.name}</p>

                {data.registryAutofill && data.registryAutofill !== "ok" && (
                  // The registry could not fill the form; the identity comes from the
                  // Smart-ID session and the right to register is decided on submit —
                  // so the person is told, not stopped (review pr22 #2).
                  <div
                    role="status"
                    className="bg-muted text-muted-foreground text-sm p-4 rounded-xl border border-border mb-4 flex items-start gap-2"
                  >
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{t(`onboarding.provider.autofill.${data.registryAutofill}`)}</span>
                  </div>
                )}

                <section className="mb-6">
                  <div className="text-xs font-medium text-muted-foreground uppercase mb-2">
                    {t(
                      data.registryAutofill && data.registryAutofill !== "ok"
                        ? "onboarding.provider.sessionData"
                        : "onboarding.provider.registryData"
                    )}
                  </div>
                  <dl className="space-y-1 text-sm">
                    <Row k={t("onboarding.building.name")} v={data.name} />
                    <Row k={t("onboarding.building.registryCode")} v={data.registryCode} />
                    {data.legalForm && <Row k={t("onboarding.provider.legalForm")} v={data.legalForm} />}
                    {data.address && <Row k={t("onboarding.building.address")} v={data.address} />}
                    {data.foundingDate && (
                      <Row
                        k={t("onboarding.provider.foundingDate")}
                        v={formatDate(data.foundingDate, i18n.language) || data.foundingDate}
                      />
                    )}
                    {data.phone && <Row k={t("onboarding.building.phone")} v={data.phone} />}
                  </dl>
                </section>

                <div className="mb-6">
                  <Label htmlFor="contact-email" className="text-sm font-medium mb-2 block">
                    {t("onboarding.provider.emailLabel")} *
                  </Label>
                  <Input id="contact-email" type="email" value={email}
                         onChange={(e) => setEmail(e.target.value)} required />
                  <p className="text-xs text-muted-foreground mt-1">{t("onboarding.provider.emailHint")}</p>
                </div>

                <div className="mb-6">
                  <div className="text-sm font-medium mb-2">
                    {t("onboarding.provider.servicesLabel")} *
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{t("onboarding.provider.servicesHint")}</p>
                  <CategoryCheckboxTree
                    categories={data.availableServices}
                    value={services}
                    onChange={setServices}
                  />
                </div>

                {error && (
                  <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-xl border border-destructive/20 mb-4 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    {error}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => navigate("/select-role")}>
                    {t("onboarding.provider.cancel")}
                  </Button>
                  <Button className="flex-1" onClick={handleSubmit} disabled={!canSubmit}>
                    {t("onboarding.provider.submit")}
                  </Button>
                </div>
              </>
            )}

            {step === "confirm" && data && data.alreadyExists && (
              <>
                <h1 className="text-2xl font-semibold mb-4">
                  {t("selectRole.joinConfirm.title")}
                </h1>
                <p className="text-sm text-muted-foreground mb-6">
                  {t("selectRole.joinConfirm.text", {
                    name: data.name,
                    repType: t("selectRole.joinConfirm.repType.rep"),
                  })}
                </p>
                {error && (
                  <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-xl border border-destructive/20 mb-4 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    {error}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => navigate("/select-role")}>
                    {t("selectRole.joinConfirm.cancel")}
                  </Button>
                  <Button className="flex-1" onClick={handleConfirmJoin}>
                    {t("selectRole.joinConfirm.confirm")}
                  </Button>
                </div>
              </>
            )}

            {step === "submitting" && (
              <div className="flex flex-col items-center py-16 gap-4">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
            )}

            {step === "success" && (
              <div className="flex flex-col items-center py-16 gap-4">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
                <p className="text-foreground font-medium">{t("onboarding.provider.success")}</p>
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-border">
              <Button variant="ghost" className="w-full" onClick={() => navigate("/select-role")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("onboarding.back")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="text-foreground font-medium text-right">{v}</dd>
    </div>
  );
}
